import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Sparkles, Mail, Lock, Eye, EyeOff, Loader2, ArrowRight } from 'lucide-react'
import { signUp, signInWithGoogle } from '../../utils/auth'

export default function SignUp() {
    const navigate = useNavigate()
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [status, setStatus] = useState('idle') // idle, loading, success, error
    const [errorMessage, setErrorMessage] = useState('')
    const [googleLoading, setGoogleLoading] = useState(false)

    const validateForm = () => {
        if (!email || !password || !confirmPassword) {
            setErrorMessage('Please fill in all fields')
            return false
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setErrorMessage('Please enter a valid email address')
            return false
        }
        if (password.length < 8) {
            setErrorMessage('Password must be at least 8 characters')
            return false
        }
        if (password !== confirmPassword) {
            setErrorMessage('Passwords do not match')
            return false
        }
        return true
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setErrorMessage('')

        if (!validateForm()) {
            setStatus('error')
            return
        }

        setStatus('loading')

        try {
            const { data, error } = await signUp(email, password)

            if (error) {
                throw new Error(error.message)
            }

            setStatus('success')

            // Send welcome email
            const APP_URL = import.meta.env.VITE_APP_URL || window.location.origin
            fetch(`${APP_URL}/api/email/welcome`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            }).catch(console.error)

            // Redirect after a short delay to show success message
            setTimeout(() => navigate('/signin'), 2000)
        } catch (error) {
            setStatus('error')
            setErrorMessage(error.message || 'Failed to create account')
        }
    }

    const handleGoogleSignUp = async () => {
        setGoogleLoading(true)
        setErrorMessage('')

        try {
            const { error } = await signInWithGoogle()
            if (error) {
                throw new Error(error.message)
            }
            // Redirect happens automatically via OAuth
        } catch (error) {
            setGoogleLoading(false)
            setStatus('error')
            setErrorMessage(error.message || 'Failed to sign up with Google')
        }
    }

    return (
        <div className="min-h-screen flex items-center justify-center px-4 py-12 animated-gradient-bg">
            {/* Background Effects */}
            <div className="blob w-72 h-72 bg-primary-500/20 -top-20 -left-20" />
            <div className="blob w-96 h-96 bg-accent-500/20 -bottom-20 -right-20" />

            <div className="relative z-10 w-full max-w-md">
                {/* Logo */}
                <Link to="/" className="flex items-center justify-center gap-2 mb-8">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-400 to-accent-500 flex items-center justify-center">
                        <Sparkles className="w-7 h-7 text-white" />
                    </div>
                    <span className="text-2xl font-bold text-white">InkSpot</span>
                </Link>

                {/* Card */}
                <div className="glass-card p-8">
                    <h1 className="text-2xl font-bold text-white text-center mb-2">
                        Create your account
                    </h1>
                    <p className="text-white/60 text-center mb-8">
                        Start writing better today
                    </p>

                    {/* Google Sign Up Button */}
                    <button
                        onClick={handleGoogleSignUp}
                        disabled={googleLoading}
                        className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white hover:bg-gray-100 text-gray-800 font-medium rounded-xl transition-colors disabled:opacity-50 mb-6"
                    >
                        {googleLoading ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                            <svg className="w-5 h-5" viewBox="0 0 24 24">
                                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                            </svg>
                        )}
                        Continue with Google
                    </button>

                    {/* Divider */}
                    <div className="flex items-center gap-4 mb-6">
                        <div className="flex-grow h-px bg-white/10" />
                        <span className="text-white/40 text-sm">or continue with email</span>
                        <div className="flex-grow h-px bg-white/10" />
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* Email */}
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-2">
                                Email address
                            </label>
                            <div className="relative">
                                <input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="input-field pl-11"
                                    placeholder="you@example.com"
                                />
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                            </div>
                        </div>

                        {/* Password */}
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-white/80 mb-2">
                                Password
                            </label>
                            <div className="relative">
                                <input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="input-field pl-11 pr-11"
                                    placeholder="Min. 8 characters"
                                />
                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>

                        {/* Confirm Password */}
                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-white/80 mb-2">
                                Confirm password
                            </label>
                            <div className="relative">
                                <input
                                    id="confirmPassword"
                                    type={showPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="input-field pl-11"
                                    placeholder="Re-enter password"
                                />
                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                            </div>
                        </div>

                        {/* Error Message */}
                        {status === 'error' && (
                            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                                <p className="text-sm text-red-400">{errorMessage}</p>
                            </div>
                        )}

                        {/* Success Message */}
                        {status === 'success' && (
                            <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                                <p className="text-sm text-green-400">
                                    Account created! Check your email for verification.
                                </p>
                            </div>
                        )}

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={status === 'loading' || status === 'success'}
                            className="btn-primary w-full disabled:opacity-50"
                        >
                            {status === 'loading' ? (
                                <>
                                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                    Creating account...
                                </>
                            ) : (
                                <>
                                    Create account
                                    <ArrowRight className="w-5 h-5 ml-2" />
                                </>
                            )}
                        </button>
                    </form>

                    {/* Sign In Link */}
                    <p className="text-center text-white/60 mt-6">
                        Already have an account?{' '}
                        <Link to="/signin" className="text-primary-400 hover:text-primary-300 font-medium">
                            Sign in
                        </Link>
                    </p>
                </div>

                {/* Terms */}
                <p className="text-center text-white/40 text-xs mt-6">
                    By creating an account, you agree to our{' '}
                    <a href="#" className="underline hover:text-white/60">Terms of Service</a>
                    {' '}and{' '}
                    <a href="#" className="underline hover:text-white/60">Privacy Policy</a>
                </p>
            </div>
        </div>
    )
}
